import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  Grid,
  Alert,
  AlertTitle,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  LinearProgress,
  CircularProgress,
  Divider,
  Paper,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  DeviceHub as DeviceIcon,
  Videocam as CameraIcon,
  Policy as PolicyIcon,
  CloudUpload as DeployIcon,
  Verified as VerifyIcon,
  MenuBook as DocsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  PlayArrow as TestIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { api, ensureUiScope, endpoints } from '../services/api';
import { formatRegistryCameraDeleteError } from '../services/camerasApi';
import { sitesApi, type PilotCaps, type SiteComplexity } from '../services/sitesApi';
import { getPolicyDeploymentMetadata, type PolicyDeploymentState } from '../utils/statusMetadata';
import { useAuth } from '../contexts/AuthContext';
import { EnterpriseDeployJob } from '../components/enterpriseDeploy/enterpriseDeployShared';
import {
  isSetupContinueBlocked,
  SetupProductionReadyGate,
} from '../components/enterpriseDeploy/SetupProductionReadyGate';
import {
  productionReadyChipColor,
  productionReadyChipLabel,
} from '../components/enterpriseDeploy/setupProductionReady';

// Types
interface Device {
  device_id: string;
  display_name: string;
  status: string;
  site_id: string;
  org_id?: string;
}

interface Camera {
  camera_id: string;
  mac_address: string;
  serial_number?: string;
  name: string;
  uri: string;
  source_type?: string;
  enabled: boolean;
  status?: string;
}

interface CameraPipelineStatus {
  camera_id: string;
  pid?: number | null;
  alive: boolean;
  pipeline?: string;
  source_type?: string;
  rtsp_url?: string | null;
  runtime_desired_state?: string | null;
  runtime_override?: any;
}

interface Policy {
  policy_id: string;
  policy_type: string;
  name: string;
  enabled: boolean;
  severity: string;
  camera_id?: string;
  edge_device_id?: string;
  zone_ids?: string[];
  deployment_state?: PolicyDeploymentState;
  deployment_reason?: string;
}

interface ZoneSummary {
  zone_id: string;
  name: string;
  camera_id: string;
  enabled: boolean;
}

const POLICY_TYPES = [
  { type: 'zone_intrusion', name: 'Zone Intrusion', description: 'Alert when person enters restricted zone' },
  { type: 'ppe', name: 'PPE Compliance', description: 'Alert when required PPE is missing' },
  { type: 'dwell', name: 'Dwell Time', description: 'Alert when person stays stationary too long' },
  { type: 'loitering', name: 'Loitering', description: 'Alert when person lingers in area' },
] as const;

const POLICY_TEMPLATES: Record<
  string,
  {
    defaultSeverity: 'low' | 'medium' | 'high' | 'critical';
    namePrefix: string;
    help: string;
  }
> = {
  ppe: {
    defaultSeverity: 'high',
    namePrefix: 'PPE Compliance',
    help: 'Use for PPE requirements (helmet/vest/etc). Apply to the cameras where PPE is mandatory.',
  },
  zone_intrusion: {
    defaultSeverity: 'critical',
    namePrefix: 'Zone Intrusion',
    help: 'Triggers when a person enters a restricted zone. Zones are configured per camera (ROI).',
  },
  dwell: {
    defaultSeverity: 'medium',
    namePrefix: 'Dwell Time',
    help: 'Triggers when a person stays in an area beyond a threshold (good for queueing, blocked aisles, etc).',
  },
  loitering: {
    defaultSeverity: 'medium',
    namePrefix: 'Loitering',
    help: 'Triggers when someone lingers in an area; use for entrances, restricted corridors, etc.',
  },
};

const WIZARD_TO_API_POLICY_TYPE: Record<string, string> = {
  ppe: 'ppe_compliance',
  dwell: 'dwell_violation',
  loitering: 'loitering',
  zone_intrusion: 'zone_intrusion',
};

const ZONE_SCOPED_POLICY_TYPES = new Set(['zone_intrusion', 'dwell_violation', 'loitering']);

const WIZARD_STEPS = [
  { label: 'Select Device', icon: <DeviceIcon /> },
  { label: 'Configure Cameras', icon: <CameraIcon /> },
  { label: 'Setup Policies', icon: <PolicyIcon /> },
  { label: 'Deploy Configuration', icon: <DeployIcon /> },
  { label: 'Verify Live View', icon: <CameraIcon /> },
  { label: 'Verify Connection', icon: <VerifyIcon /> },
];

const SetupWizard: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { effectiveUser: user } = useAuth();
  const capabilities = Array.isArray((user as any)?.capabilities) ? (user as any).capabilities : [];
  const canWriteDevices = capabilities.includes('devices:write');
  const canWriteCameras = capabilities.includes('cameras:write');
  const canWritePolicies = capabilities.includes('policies:write');
  const canWriteDeployments = capabilities.includes('deployments:write');
  const canReadDeployments = capabilities.includes('deployments:read');
  const canMutateSetup = canWriteDevices && canWriteCameras && canWritePolicies && canWriteDeployments;
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pilot-mode governance (site-level)
  const [pilotMode, setPilotMode] = useState(false);
  const [pilotCaps, setPilotCaps] = useState<PilotCaps>({
    max_zones: 10,
    max_policies: 10,
    max_policies_per_camera: 2,
  });
  const [complexity, setComplexity] = useState<SiteComplexity | null>(null);
  const [pilotSaving, setPilotSaving] = useState(false);
  const [selectedDeviceCounts, setSelectedDeviceCounts] = useState<{
    zones: number;
    policies: number;
  } | null>(null);
  const [selectedDeviceZones, setSelectedDeviceZones] = useState<ZoneSummary[]>([]);

  // Gate 4: site config export hooks (UI convenience; CLI remains source of truth)
  const [exportingSiteConfig, setExportingSiteConfig] = useState(false);
  const [exportSiteConfigError, setExportSiteConfigError] = useState<string | null>(null);
  const [siteConfigDialogOpen, setSiteConfigDialogOpen] = useState(false);
  const [siteConfigJson, setSiteConfigJson] = useState<any>(null);

  // Device state
  const [devices, setDevices] = useState<Device[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const selectedDeviceIdRef = useRef<string>('');
  const [enterpriseJobsById, setEnterpriseJobsById] = useState<
    Record<string, EnterpriseDeployJob | null>
  >({});

  // Camera state
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
  const [cameraTestSubmitting, setCameraTestSubmitting] = useState<Record<string, boolean>>({});
  const [cameraTestResults, setCameraTestResults] = useState<
    Record<string, { ok: boolean; message: string; testedAtMs: number }>
  >({});
  const [cameraTestToast, setCameraTestToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [newCamera, setNewCamera] = useState({
    mac_address: '',
    serial_number: '',
    name: '',
    uri: '',
    source_type: 'rtsp' as 'rtsp' | 'hardware',
    rtsp_username: '',
    rtsp_password: '',
    camera_id: '',
  });

  // Policy state
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [hiddenStalePolicies, setHiddenStalePolicies] = useState<number>(0);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [newPolicy, setNewPolicy] = useState({
    policy_type: 'ppe',
    name: '',
    severity: 'medium',
    camera_id: '',
  });

  const _policyTemplateFor = (policyType: string) =>
    POLICY_TEMPLATES[policyType] || {
      defaultSeverity: 'medium' as const,
      namePrefix: policyType,
      help: '',
    };

  // Deploy state
  const [deployStatus, setDeployStatus] = useState<'idle' | 'deploying' | 'success' | 'error'>('idle');
  const [deployMessage, setDeployMessage] = useState('');
  const [deployPreviewLoading, setDeployPreviewLoading] = useState(false);
  const [deployPreviewError, setDeployPreviewError] = useState<string | null>(null);
  const [deployPreview, setDeployPreview] = useState<any>(null);
  // Epic 3 Phase 3: blast radius (site device count) for deploy confirmation
  const [scopeBlastRadius, setScopeBlastRadius] = useState<{ device_count: number; org_id: string; site_id: string } | null>(null);

  // Verify Live View state (Phase C.1)
  const [liveViewStatus, setLiveViewStatus] = useState<'idle' | 'checking' | 'success' | 'error'>('idle');
  const [liveViewResults, setLiveViewResults] = useState<Record<string, any>>({});

  // Verify Connection state
  const [verifyStatus, setVerifyStatus] = useState<'idle' | 'checking' | 'success' | 'error'>('idle');
  const [verifyResults, setVerifyResults] = useState<any>(null);
  const [selectedDeviceStatus, setSelectedDeviceStatus] = useState<any>(null);
  const [selectedDeviceSync, setSelectedDeviceSync] = useState<any>(null);

  // Per-camera pipelines (Model A)
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [pipelineCameras, setPipelineCameras] = useState<CameraPipelineStatus[]>([]);
  const [pipelineRunningCount, setPipelineRunningCount] = useState<number>(0);
  const [pipelineActionCameraId, setPipelineActionCameraId] = useState<string | null>(null);
  const pipelinePollRef = useRef<NodeJS.Timeout | null>(null);
  const cameraNameById = useMemo(() => {
    const out: Record<string, string> = {};
    for (const c of cameras || []) {
      const cid = String(c?.camera_id || '').trim();
      if (!cid) continue;
      const nm = String(c?.name || '').trim();
      out[cid] = nm || cid;
    }
    return out;
  }, [cameras]);
  const activeCameraIdSet = useMemo(() => {
    const out = new Set<string>();
    for (const c of cameras || []) {
      const cid = String(c?.camera_id || '').trim();
      if (cid) out.add(cid);
    }
    return out;
  }, [cameras]);

  // Load devices on mount
  useEffect(() => {
    loadDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only
  }, []);

  useEffect(() => {
    selectedDeviceIdRef.current = String(selectedDevice?.device_id || '').trim();
  }, [selectedDevice?.device_id]);

  // Epic 3 Phase 3 / Epic 11: fetch blast radius (site device count) and canary cohort for deploy confirmation
  const [scopeCanaryCount, setScopeCanaryCount] = useState<number | null>(null);
  useEffect(() => {
    const orgId = String(selectedDevice?.org_id || '').trim();
    const siteId = String(selectedDevice?.site_id || '').trim();
    if (!orgId || !siteId) {
      setScopeBlastRadius(null);
      setScopeCanaryCount(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      api.get('/api/v1/deployments/scope', { params: { org_id: orgId, site_id: siteId } }),
      api.get(endpoints.devices.groups).catch(() => ({ data: { groups: [] } })),
    ]).then(([scopeRes, groupsRes]) => {
      if (cancelled) return;
      const d = scopeRes?.data;
      if (d && typeof d.device_count === 'number') {
        setScopeBlastRadius({ device_count: d.device_count, org_id: orgId, site_id: siteId });
      } else {
        setScopeBlastRadius(null);
      }
      const groups: Array<{ id: string; org_id?: string; site_id?: string; tags?: Record<string, unknown>; name?: string }> =
        groupsRes?.data?.groups || groupsRes?.data?.items || [];
      const canary = groups.find(
        (g) =>
          String(g.org_id || '').trim() === orgId &&
          String(g.site_id || '').trim() === siteId &&
          (String((g.tags as Record<string, unknown>)?.cohort || '').toLowerCase() === 'canary' ||
            String(g.name || '').toLowerCase().includes('canary'))
      );
      if (!canary) {
        setScopeCanaryCount(null);
        return;
      }
      api
        .get('/api/v1/deployments/scope', {
          params: { org_id: orgId, site_id: siteId, group_id: canary.id, include_device_ids: false },
        })
        .then((r) => {
          if (!cancelled && r?.data && typeof r.data.device_count === 'number') {
            setScopeCanaryCount(r.data.device_count);
          }
        })
        .catch(() => {
          if (!cancelled) setScopeCanaryCount(null);
        });
    }).catch(() => {
      if (!cancelled) {
        setScopeBlastRadius(null);
        setScopeCanaryCount(null);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [selectedDevice?.org_id, selectedDevice?.site_id]);

  // Setup Wizard context is per selected device, but pilot complexity is site-level.
  // Show per-device counts too so operators don't confuse site totals with a fresh unit.
  useEffect(() => {
    if (!selectedDevice?.device_id) {
      setSelectedDeviceCounts(null);
      setSelectedDeviceZones([]);
      return;
    }
    const siteId = String(selectedDevice.site_id || '').trim();
    if (!siteId) {
      setSelectedDeviceCounts((prev) => ({
        zones: 0,
        policies: prev?.policies ?? 0,
      }));
      setSelectedDeviceZones([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const zr = await api.get('/api/v1/zones', {
          params: {
            site_id: siteId,
            device_id: selectedDevice.device_id,
            include_disabled: true,
          },
        });
        const zonesListRaw = Array.isArray(zr?.data?.zones) ? zr.data.zones : [];
        const zonesList = zonesListRaw.map((z: any) => ({
          zone_id: String(z?.zone_id || '').trim(),
          name: String(z?.name || z?.zone_id || '').trim(),
          camera_id: String(z?.camera_id || '').trim(),
          enabled: z?.enabled !== false,
        }));
        const zones =
          typeof zr?.data?.count === 'number' ? Number(zr.data.count) : zonesList.length;
        if (!cancelled) {
          setSelectedDeviceCounts((prev) => ({
            zones: Number.isFinite(zones) ? zones : 0,
            policies: prev?.policies ?? 0,
          }));
          setSelectedDeviceZones(zonesList);
        }
      } catch {
        if (!cancelled) {
          setSelectedDeviceCounts((prev) => ({
            zones: 0,
            policies: prev?.policies ?? 0,
          }));
          setSelectedDeviceZones([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedDevice?.device_id, selectedDevice?.site_id]);

  useEffect(() => {
    if (!selectedDevice?.device_id) return;
    setSelectedDeviceCounts((prev) => ({
      zones: prev?.zones ?? 0,
      policies: policies.length,
    }));
  }, [selectedDevice?.device_id, policies.length]);

  // Load pilot-mode config from default site (best-effort).
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const s = await sitesApi.getDefaultSite();
        const cfg = (s?.config && typeof s.config === 'object' ? s.config : {}) as any;
        const pm = cfg?.pilot_mode === true;
        const caps = cfg?.pilot_caps && typeof cfg.pilot_caps === 'object' ? cfg.pilot_caps : {};
        if (!mounted) return;
        setPilotMode(pm);
        setPilotCaps({
          max_zones: typeof caps.max_zones === 'number' ? caps.max_zones : 10,
          max_policies: typeof caps.max_policies === 'number' ? caps.max_policies : 10,
          max_policies_per_camera:
            typeof caps.max_policies_per_camera === 'number' ? caps.max_policies_per_camera : 2,
        });
        const c = await sitesApi.getComplexity(s.site_id || 'default');
        if (mounted) setComplexity(c);
      } catch {
        // optional; safe to ignore if sites API is gated by auth in some deployments
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const savePilotConfig = async (nextPilotMode: boolean, nextCaps: PilotCaps) => {
    setPilotSaving(true);
    try {
      const s = await sitesApi.getDefaultSite();
      await sitesApi.updateSite(s.site_id || 'default', {
        config: {
          pilot_mode: nextPilotMode,
          pilot_caps: nextCaps,
        },
      });
      const c = await sitesApi.getComplexity(s.site_id || 'default');
      setComplexity(c);
      setPilotMode(nextPilotMode);
      setPilotCaps(nextCaps);
    } catch (e: any) {
      setError(e?.message || 'Failed to save pilot mode');
    } finally {
      setPilotSaving(false);
    }
  };

  // If opened from Devices page, allow preselect via query param: /setup?device=<device_id>
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const desired = (params.get('device') || '').trim();
    if (!desired || !devices.length) return;
    if (selectedDevice?.device_id === desired) return;
    const found = devices.find((d) => d.device_id === desired);
    if (found) {
      void handleSelectDevice(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, devices]);

  // Optional deep-link step: /setup?device=<id>&step=<index>
  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const stepRaw = (params.get('step') || '').trim();
    if (!stepRaw) return;
    const parsed = Number(stepRaw);
    if (!Number.isFinite(parsed)) return;
    const clamped = Math.max(0, Math.min(WIZARD_STEPS.length - 1, Math.floor(parsed)));
    setActiveStep((prev) => (prev === clamped ? prev : clamped));
  }, [location.search]);

  const loadDevices = async () => {
    try {
      const response = await api.get('/api/v1/devices/');
      const items = response.data.devices || [];
      setDevices(items);
      // Device setup: ensure device exists when opened via /setup?device=<id>
      // Setup Wizard configures edge devices; create in DB if missing.
      const params = new URLSearchParams(location.search || '');
      const desired = (params.get('device') || '').trim();
      if (desired && !items.some((d: any) => d.device_id === desired)) {
        try {
          const scope = ensureUiScope();
          const payload: Record<string, unknown> = { device_id: desired };
          if (scope.orgId) payload.org_id = scope.orgId;
          if (scope.siteId) payload.site_id = scope.siteId;
          await api.post(endpoints.devices.ensure, payload);
          const res2 = await api.get('/api/v1/devices/');
          setDevices(res2.data.devices || []);
        } catch {
          // non-blocking
        }
      }
    } catch (err) {
      console.error('Failed to load devices:', err);
    }
  };

  const loadEnterpriseJob = async (deviceId: string): Promise<EnterpriseDeployJob | null> => {
    if (!canReadDeployments || !deviceId) return null;
    try {
      const res = await api.get(endpoints.enterpriseDeploy.latest(deviceId));
      return (res.data as EnterpriseDeployJob) || null;
    } catch {
      return null;
    }
  };

  const refreshEnterpriseJobsForDevices = async (deviceRows: Device[]) => {
    if (!canReadDeployments || deviceRows.length === 0) {
      setEnterpriseJobsById({});
      return;
    }
    const entries = await Promise.all(
      deviceRows.map(async (d) => {
        const id = String(d.device_id || '').trim();
        const job = id ? await loadEnterpriseJob(id) : null;
        return [id, job] as const;
      }),
    );
    const next: Record<string, EnterpriseDeployJob | null> = {};
    for (const [id, job] of entries) {
      if (id) next[id] = job;
    }
    setEnterpriseJobsById(next);
  };

  useEffect(() => {
    void refreshEnterpriseJobsForDevices(devices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devices, canReadDeployments]);

  const loadCameras = async (deviceId: string): Promise<Camera[]> => {
    try {
      const response = await api.get(`/api/v1/registry/devices/${deviceId}/cameras`);
      // API returns { ok, items: [...] }
      const items = response.data.items || [];
      const parsed = items.map((c: any) => {
        const sourceType = (() => {
          const raw = String(c?.source?.type || '').trim().toLowerCase();
          if (raw === 'rtsp' || raw === 'hardware') return raw;
          const uri = String(c?.source?.uri || c?.source?.url || '').trim().toLowerCase();
          if (uri.startsWith('rtsp://') || uri.startsWith('rtsps://')) return 'rtsp';
          if (String(c?.camera_id || '').trim().toLowerCase() === 'hardware_cam') return 'hardware';
          return 'unknown';
        })();
        return {
          camera_id: c.camera_id,
          mac_address: c.source?.mac_address || '',
          serial_number: c.source?.serial_number || '',
          name: c.display_name || c.camera_id,
          uri: c.source?.uri || c.source?.url || '',
          source_type: sourceType,
          enabled: c.health?.status !== 'disabled',
          status: c.health?.status,
        };
      });
      if (selectedDeviceIdRef.current === String(deviceId)) {
        setCameras(parsed);
      }
      return parsed;
    } catch (err) {
      console.error('Failed to load cameras:', err);
      if (selectedDeviceIdRef.current === String(deviceId)) {
        setCameras([]);
      }
      return [];
    }
  };

  const loadPolicies = async (
    deviceId: string,
    siteId: string,
    cameraIds?: Set<string>,
    orgId?: string
  ) => {
    if (!siteId) {
      if (selectedDeviceIdRef.current === String(deviceId)) {
        setPolicies([]);
        setHiddenStalePolicies(0);
      }
      return;
    }
    try {
      const params: Record<string, unknown> = { limit: 500 };
      if (orgId && orgId.trim()) params.org_id = orgId.trim();
      const response = await api.get(`/api/v1/sites/${siteId}/policies`, {
        params,
      });
      const items = response.data.policies || [];
      const filtered = items.filter((p: any) => {
        const target = p?.target && typeof p.target === 'object' ? p.target : {};
        const edgeDeviceId = target?.edge_device_id || p?.edge_device_id;
        return edgeDeviceId === deviceId;
      });
      const normalized: Policy[] = filtered.map((p: any) => {
        const target = p?.target && typeof p.target === 'object' ? p.target : {};
        const scope = p?.scope && typeof p.scope === 'object' ? p.scope : {};
        const apiType = String(p?.policy_type || '').trim();
        const cameraId =
          target?.camera_id ||
          p?.camera_id ||
          (Array.isArray(scope?.camera_ids) && scope.camera_ids.length === 1 ? scope.camera_ids[0] : '');
        const zoneIds = Array.isArray(scope?.zone_ids)
          ? scope.zone_ids.filter((z: any) => typeof z === 'string' && z.trim())
          : [];
        return {
          policy_id: p.policy_id,
          policy_type: apiType || 'unknown',
          name: p.name || p.display_name || apiType,
          enabled: Boolean(p.enabled),
          severity: p.alert?.severity || p.config?.severity || 'medium',
          camera_id: cameraId || undefined,
          edge_device_id: target?.edge_device_id || p?.edge_device_id || undefined,
          zone_ids: zoneIds,
          deployment_state: p?.deployment_state as PolicyDeploymentState | undefined,
          deployment_reason: typeof p?.deployment_reason === 'string' ? p.deployment_reason : undefined,
        };
      });

      // Hide stale policies bound to old camera IDs that are not currently configured
      // on the selected device (common when reusing a logical device_id for a fresh unit).
      if (cameraIds) {
        const visible = normalized.filter((p) => !p.camera_id || cameraIds.has(String(p.camera_id)));
        if (selectedDeviceIdRef.current === String(deviceId)) {
          setHiddenStalePolicies(Math.max(0, normalized.length - visible.length));
          setPolicies(visible);
        }
      } else {
        if (selectedDeviceIdRef.current === String(deviceId)) {
          setHiddenStalePolicies(0);
          setPolicies(normalized);
        }
      }
    } catch (err) {
      console.error('Failed to load policies:', err);
      if (selectedDeviceIdRef.current === String(deviceId)) {
        setPolicies([]);
        setHiddenStalePolicies(0);
      }
    }
  };

  const downloadJson = (filename: string, obj: any) => {
    try {
      const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setExportSiteConfigError(e?.message || 'Failed to download file');
    }
  };

  const handleExportSiteConfig = async () => {
    if (!selectedDevice?.device_id) return;
    setExportingSiteConfig(true);
    setExportSiteConfigError(null);
    try {
      const deviceId = selectedDevice.device_id;
      const siteId = String(selectedDevice.site_id || '').trim();
      const orgId = String(selectedDevice.org_id || '').trim();
      if (!siteId) throw new Error('Selected device is missing site scope');

      const zonesResp = await api.get(`/api/v1/zones`, {
        params: { site_id: siteId, device_id: deviceId, include_disabled: true },
      });

      const zones = (zonesResp.data?.zones || []).map((z: any) => {
        const poly = Array.isArray(z.polygon_norm) ? z.polygon_norm : null;
        const bbox = Array.isArray(z.bbox_norm) ? z.bbox_norm : null;
        const rectPoly =
          bbox && bbox.length === 4
            ? [
                [bbox[0], bbox[1]],
                [bbox[0] + bbox[2], bbox[1]],
                [bbox[0] + bbox[2], bbox[1] + bbox[3]],
                [bbox[0], bbox[1] + bbox[3]],
              ]
            : [];
        return {
          zone_id: z.zone_id,
          name: z.name,
          camera_id: z.camera_id,
          severity: z.severity || 'medium',
          restricted: Boolean(z.restricted),
          polygon_norm: poly || rectPoly,
        };
      });

      const siteConfig = {
        schema_version: 'site_config.v1',
        org_id: orgId || undefined,
        site_id: siteId,
        timezone: 'UTC',
        devices: [{ device_id: deviceId, name: selectedDevice.display_name || deviceId }],
        cameras: (cameras || []).map((c) => ({
          camera_id: c.camera_id,
          name: c.name,
          device_id: deviceId,
          enabled: Boolean(c.enabled),
          rtsp_url: c.uri || null,
          credentials_ref: null,
        })),
        zones,
        policies: (policies || []).map((p) => ({
          name: p.name,
          policy_type: p.policy_type,
          enabled: Boolean(p.enabled),
          camera_id: p.camera_id || null,
          zone_id: null,
          evidence: { snapshot: true, clip: false },
        })),
      };

      setSiteConfigJson(siteConfig);
      setSiteConfigDialogOpen(true);
    } catch (e: any) {
      setExportSiteConfigError(e?.response?.data?.error || e?.message || 'Failed to export site config');
    } finally {
      setExportingSiteConfig(false);
    }
  };

  // Step 1: Device Selection
  const handleSelectDevice = async (device: Device) => {
    const deviceId = String(device.device_id || '').trim();
    selectedDeviceIdRef.current = deviceId;
    setSelectedDevice(device);
    setCameras([]);
    setPolicies([]);
    setHiddenStalePolicies(0);
    setPipelineCameras([]);
    setPipelineRunningCount(0);
    setPipelineError(null);
    setLoading(true);
    try {
      const parsedCameras = await loadCameras(deviceId);
      if (selectedDeviceIdRef.current !== deviceId) return;
      const parsedCameraIds = new Set(
        (parsedCameras || [])
          .map((c) => String(c?.camera_id || '').trim())
          .filter((v) => Boolean(v))
      );
      await Promise.all([
        loadPolicies(
          deviceId,
          String(device.site_id || '').trim(),
          parsedCameraIds,
          String(device.org_id || '').trim() || undefined
        ),
        loadPipelines(deviceId),
      ]);
      if (selectedDeviceIdRef.current !== deviceId) return;
      try {
        const resp = await api.get(`/api/v1/registry/devices/${deviceId}/status`);
        if (selectedDeviceIdRef.current === deviceId) {
          setSelectedDeviceStatus(resp.data);
        }
      } catch {
        if (selectedDeviceIdRef.current === deviceId) {
          setSelectedDeviceStatus(null);
        }
      }
      try {
        const resp = await api.get(`/api/v1/registry/devices/${deviceId}/sync_status`);
        if (selectedDeviceIdRef.current === deviceId) {
          setSelectedDeviceSync(resp.data);
        }
      } catch {
        if (selectedDeviceIdRef.current === deviceId) {
          setSelectedDeviceSync(null);
        }
      }
      if (canReadDeployments) {
        const entJob = await loadEnterpriseJob(deviceId);
        if (selectedDeviceIdRef.current === deviceId) {
          setEnterpriseJobsById((prev) => ({ ...prev, [deviceId]: entJob }));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Decommission and delete a device
  const handleDeleteDevice = async (deviceId: string) => {
    if (!window.confirm(
      `Decommission device "${deviceId}"?\n\n` +
      `This will:\n` +
      `• Revoke all credentials immediately\n` +
      `• Notify edge device to clear local state\n` +
      `• Remove device from cloud\n\n` +
      `This cannot be undone.`
    )) {
      return;
    }
    setLoading(true);
    try {
      // Step 1: Decommission (revoke credentials + notify edge)
      const decommResult = await api.post(`/api/v1/provisioning/devices/${deviceId}/decommission`);
      console.log('Decommission result:', decommResult.data);
      
      // Step 2: Delete the device record
      await api.delete(`/api/v1/devices/${deviceId}`);
      
      await loadDevices();
      // Clear selection if deleted device was selected
      if (selectedDevice?.device_id === deviceId) {
        setSelectedDevice(null);
      }
      
      // Show success message with MQTT notification status
      if (decommResult.data?.mqtt_notified) {
        alert(`Device "${deviceId}" decommissioned and deleted.\nEdge device was notified to clear credentials.`);
      } else {
        alert(`Device "${deviceId}" deleted.\nNote: Could not notify edge device (${decommResult.data?.mqtt_error || 'offline'}). You may need to manually clear credentials on the device.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to decommission device');
    } finally {
      setLoading(false);
    }
  };

  // Check device provisioning status
  const handleCheckDeviceStatus = async (deviceId: string) => {
    try {
      const response = await api.get(`/api/v1/provisioning/devices/${deviceId}/status`);
      const status = response.data;
      
      // Update device in list
      setDevices(devices.map(d => 
        d.device_id === deviceId 
          ? { ...d, status: status.provisioning_status === 'activated' ? 'online' : 'pending_activation' }
          : d
      ));
      
      // Update selected device if it's the one we checked
      if (selectedDevice?.device_id === deviceId && status.provisioning_status === 'activated') {
        setSelectedDevice({ ...selectedDevice, status: 'online' });
      }
      
      return status;
    } catch (err) {
      console.error('Failed to check device status:', err);
      return null;
    }
  };

  // Step 2: Camera Configuration
  const handleAddCamera = async () => {
    if (!selectedDevice) return;
    const sourceType = (newCamera.source_type || 'rtsp') as 'rtsp' | 'hardware';
    const trimmedUri = (newCamera.uri || '').trim();
    if (sourceType === 'rtsp' && !trimmedUri) {
      setError('RTSP URI is required');
      return;
    }
    if (sourceType === 'rtsp') {
      if (!/^rtsps?:\/\//i.test(trimmedUri)) {
        setError('RTSP URI must start with rtsp:// or rtsps://');
        return;
      }
      // Prevent embedding secrets into desired-state (must use separate credential fields).
      if (/\/\/[^/]*@/.test(trimmedUri)) {
        setError('Do not include username/password in the RTSP URI. Use the fields below.');
        return;
      }
    }
    setLoading(true);
    try {
      const identifier = (newCamera.mac_address || newCamera.serial_number || '').trim();
      const existingIds = new Set((cameras || []).map(c => c.camera_id));
      let camera_id = '';
      if (sourceType === 'hardware') {
        camera_id = (newCamera.camera_id || 'hardware_cam').trim() || 'hardware_cam';
        if (!/^[a-zA-Z0-9_-]+$/.test(camera_id)) {
          setError('Hardware Camera ID must contain only letters, numbers, underscore, or hyphen');
          return;
        }
        if (existingIds.has(camera_id)) {
          setError(`Camera ID "${camera_id}" already exists on this device`);
          return;
        }
      } else {
        // RTSP camera: prefer MAC/serial derived id, else fall back to incremental
        if (newCamera.mac_address) {
          camera_id = `cam_${newCamera.mac_address.replace(/:/g, '').slice(-6).toLowerCase()}`;
        } else if (newCamera.serial_number) {
          camera_id = `cam_${newCamera.serial_number.slice(-8).toLowerCase()}`;
        } else {
          camera_id = `cam_rtsp_${cameras.length + 1}`;
        }
      }
      
      await api.post(`/api/v1/registry/devices/${selectedDevice.device_id}/cameras`, {
        camera_id: camera_id,
        display_name: newCamera.name || `Camera ${cameras.length + 1}`,
        slug: identifier || camera_id,
        source: {
          type: sourceType,
          uri: sourceType === 'rtsp' ? trimmedUri : undefined,
          auth: sourceType === 'rtsp' ? {
            username: (newCamera.rtsp_username || '').trim() || undefined,
            password: (newCamera.rtsp_password || '').trim() || undefined,
          } : undefined,
          mac_address: newCamera.mac_address || undefined,
          serial_number: newCamera.serial_number || undefined,
        },
      });
      await loadCameras(selectedDevice.device_id);
      setCameraDialogOpen(false);
      setNewCamera({ mac_address: '', serial_number: '', name: '', uri: '', source_type: 'rtsp', rtsp_username: '', rtsp_password: '', camera_id: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add camera');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCamera = async (cameraId: string) => {
    if (!selectedDevice) return;
    try {
      await api.delete(`/api/v1/registry/devices/${selectedDevice.device_id}/cameras/${cameraId}`);
      await loadCameras(selectedDevice.device_id);
      setCameraTestResults((prev) => {
        const next = { ...prev };
        delete next[cameraId];
        return next;
      });
      setCameraTestSubmitting((prev) => {
        const next = { ...prev };
        delete next[cameraId];
        return next;
      });
    } catch (err: unknown) {
      setError(formatRegistryCameraDeleteError(err));
    }
  };

  const handleTestCamera = async (cameraId: string) => {
    if (!selectedDevice) return;
    setCameraTestSubmitting((prev) => ({ ...prev, [cameraId]: true }));
    try {
      const response = await api.post(
        `/api/v1/registry/devices/${selectedDevice.device_id}/cameras/${cameraId}/test_feed`,
        { force_probe: true, timeout_sec: 15, decode_seconds: 5 },
      );
      // Update camera status based on edge_result
      const edgeResult = response?.data?.edge_result || {};
      const ok =
        Boolean(edgeResult?.ok) ||
        String(edgeResult?.status || '').toLowerCase() === 'ready';
      const failReason =
        edgeResult?.last_error ||
        edgeResult?.error ||
        response?.data?.error ||
        response?.data?.detail ||
        edgeResult?.detail ||
        'camera test failed';
      const message = ok
        ? `Yes: camera ${cameraId} test passed`
        : `No: ${String(failReason)}`;
      setCameraTestResults((prev) => ({
        ...prev,
        [cameraId]: { ok, message, testedAtMs: Date.now() },
      }));
      setCameraTestToast({
        open: true,
        message,
        severity: ok ? 'success' : 'error',
      });
      setCameras((prev) =>
        prev.map((c) => (c.camera_id === cameraId ? { ...c, status: ok ? 'ready' : 'failed' } : c))
      );
    } catch (err: any) {
      const message = `No: ${err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'camera test failed'}`;
      setCameraTestResults((prev) => ({
        ...prev,
        [cameraId]: { ok: false, message, testedAtMs: Date.now() },
      }));
      setCameraTestToast({
        open: true,
        message,
        severity: 'error',
      });
      setCameras((prev) =>
        prev.map((c) => (c.camera_id === cameraId ? { ...c, status: 'error' } : c))
      );
    } finally {
      setCameraTestSubmitting((prev) => ({ ...prev, [cameraId]: false }));
    }
  };

  // Step 3: Policy Configuration
  const handleAddPolicy = async () => {
    if (!selectedDevice || !newPolicy.name.trim()) return;
    const siteId = selectedDevice.site_id || 'default';
    const cameraId = (newPolicy.camera_id || '').trim();
    if (!cameraId) {
      setError('Select a camera for this policy');
      return;
    }
    setLoading(true);
    try {
      // Wizard guardrail: enforce "zones first" before any policy authoring.
      const zonePresenceResp = await api.get('/api/v1/zones', {
        params: {
          site_id: siteId,
          device_id: selectedDevice.device_id,
        },
      });
      const deviceZoneCount =
        typeof zonePresenceResp?.data?.count === 'number'
          ? Number(zonePresenceResp.data.count)
          : Array.isArray(zonePresenceResp?.data?.zones)
            ? zonePresenceResp.data.zones.length
            : 0;
      if (deviceZoneCount <= 0) {
        setError('Create at least one zone (ROI) for this device before adding policies.');
        return;
      }

      const apiPolicyType =
        WIZARD_TO_API_POLICY_TYPE[newPolicy.policy_type] || newPolicy.policy_type;
      const scope: Record<string, any> = {
        camera_ids: [cameraId],
      };
      const config: Record<string, any> = {};

      if (apiPolicyType === 'ppe_compliance') {
        scope.geometry_mode = 'whole_view';
      }

      if (ZONE_SCOPED_POLICY_TYPES.has(apiPolicyType)) {
        const zonesResp = await api.get('/api/v1/zones', {
          params: {
            site_id: siteId,
            device_id: selectedDevice.device_id,
            camera_id: cameraId,
          },
        });
        const zoneIds = (zonesResp.data?.zones || [])
          .map((z: any) => (typeof z.zone_id === 'string' ? z.zone_id.trim() : ''))
          .filter((zid: string) => Boolean(zid));
        if (!zoneIds.length) {
          setError('This policy type requires at least one zone on the selected camera');
          return;
        }
        scope.zone_ids = zoneIds;
        scope.geometry_mode = 'zones';
      }

      await api.post(`/api/v1/sites/${siteId}/policies`, {
        policy_type: apiPolicyType,
        name: newPolicy.name.trim(),
        target: {
          edge_device_id: selectedDevice.device_id,
          camera_id: cameraId,
        },
        scope,
        config,
        alert: {
          severity: newPolicy.severity,
        },
        enabled: true,
      });
      await loadPolicies(selectedDevice.device_id, siteId, activeCameraIdSet, selectedDevice.org_id || 'default');
      setPolicyDialogOpen(false);
      setNewPolicy({ policy_type: 'ppe', name: '', severity: 'medium', camera_id: '' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add policy');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePolicy = async (policyId: string) => {
    if (!selectedDevice) return;
    try {
      await api.delete(`/api/v1/policies/${policyId}`, {
        params: {
          org_id: selectedDevice.org_id || 'default',
          site_id: selectedDevice.site_id || 'default',
        },
      });
      await loadPolicies(
        selectedDevice.device_id,
        selectedDevice.site_id || 'default',
        activeCameraIdSet,
        selectedDevice.org_id || 'default'
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete policy');
    }
  };

  // Step 4: Deploy
  const handleDeploy = async () => {
    if (!selectedDevice) return;
    setDeployStatus('deploying');
    setDeployMessage('Generating configuration bundle...');
    try {
      const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

      const syncDesiredStateWithFallback = async (
        deviceId: string,
        targetRevision: number
      ): Promise<{ soft?: boolean; note?: string }> => {
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            const syncResp = await api.post(
              `/api/v1/pipeline/devices/${deviceId}/edge/desired_state/sync`,
              {}
            );
            if (syncResp?.data?.ok === false || syncResp?.data?.error) {
              const detail =
                syncResp?.data?.detail ||
                syncResp?.data?.message ||
                syncResp?.data?.error ||
                'desired_state_sync_failed';
              throw new Error(`Desired-state sync failed: ${String(detail)}`);
            }
            return {};
          } catch (syncErr: any) {
            const status = Number(syncErr?.response?.status || 0);
            const code = String(syncErr?.response?.data?.error || '');
            const retryAfter = Number(syncErr?.response?.data?.retry_after_sec || 1);
            const isRateLimited = status === 429 || code === 'rate_limited';
            if (isRateLimited && attempt === 0) {
              const waitSec = Math.max(1, Math.min(10, retryAfter + 1));
              setDeployMessage(
                `Desired-state sync rate-limited by guardrail. Retrying in ${waitSec}s...`
              );
              await sleep(waitSec * 1000);
              continue;
            }

            // Soft fallback: if edge already shows this revision (or newer), treat sync as complete.
            if (isRateLimited) {
              try {
                const stateResp = await api.get(
                  `/api/v1/pipeline/devices/${deviceId}/edge/desired_state`
                );
                const desiredRevision = Number(stateResp?.data?.desired_revision || 0);
                if (Number.isFinite(desiredRevision) && desiredRevision >= targetRevision) {
                  return {
                    soft: true,
                    note: `Desired-state sync was rate-limited, but edge already reports revision ${desiredRevision}.`,
                  };
                }
              } catch {
                // Fall through to hard failure below.
              }
            }
            throw syncErr;
          }
        }
        return {};
      };

      // Generate and deploy desired state bundle
      const response = await api.post(`/api/v1/registry/devices/${selectedDevice.device_id}/deploy`);
      const revision = response.data.revision;
      setDeployMessage(`Configuration bundle created (revision ${revision}). Syncing desired state to edge...`);
      const syncOutcome = await syncDesiredStateWithFallback(selectedDevice.device_id, revision);

      setDeployStatus('success');
      setDeployMessage(
        syncOutcome.soft
          ? `Configuration deployed (revision ${revision}). ${syncOutcome.note}`
          : `Configuration deployed and synced to edge (revision ${revision})`
      );
      try {
        const resp = await api.get(`/api/v1/registry/devices/${selectedDevice.device_id}/sync_status`);
        setSelectedDeviceSync(resp.data);
      } catch {
        // ignore
      }
      // Refresh preview after deploy (it should now be "no changes").
      try {
        const preview = await api.get(`/api/v1/registry/devices/${selectedDevice.device_id}/deploy_preview`);
        setDeployPreview(preview.data);
        setDeployPreviewError(null);
      } catch {
        // ignore
      }
      void loadPipelines(selectedDevice.device_id);
    } catch (err: any) {
      setDeployStatus('error');
      setDeployMessage(
        err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          'Deployment failed'
      );
    }
  };

  const handleDeployPreview = async () => {
    if (!selectedDevice) return;
    setDeployPreviewLoading(true);
    setDeployPreviewError(null);
    try {
      const resp = await api.get(`/api/v1/registry/devices/${selectedDevice.device_id}/deploy_preview`);
      setDeployPreview(resp.data);
    } catch (err: any) {
      setDeployPreviewError(err.response?.data?.error || 'Failed to preview deploy changes');
      setDeployPreview(null);
    } finally {
      setDeployPreviewLoading(false);
    }
  };

  // Step 5: Verify
  const handleVerify = async () => {
    if (!selectedDevice) return;
    setVerifyStatus('checking');
    try {
      const response = await api.get(`/api/v1/registry/devices/${selectedDevice.device_id}/status`);
      setVerifyResults(response.data);
      setVerifyStatus(response.data.online ? 'success' : 'error');
      if (response.data.online) {
        void loadPipelines(selectedDevice.device_id);
      }
    } catch (err: any) {
      setVerifyStatus('error');
      setVerifyResults({ error: err.response?.data?.error || 'Verification failed' });
    }
  };

  // Step 5: Verify Live View (Phase C.1)
  const handleVerifyLiveView = async () => {
    if (!selectedDevice) return;
    setLiveViewStatus('checking');
    setLiveViewResults({});
    try {
      const siteId = selectedDevice.site_id || 'default';
      const deviceId = selectedDevice.device_id;
      const results: Record<string, any> = {};
      for (const cam of cameras) {
        try {
          const resp = await api.post(
            `/api/v1/sites/${siteId}/cameras/${cam.camera_id}/live_view/verify`,
                { edge_device_id: deviceId, timeout_sec: 20 }
          );
          results[cam.camera_id] = resp.data;
        } catch (e: any) {
          results[cam.camera_id] = {
            ok: false,
            verified: false,
            error: e?.response?.data?.error || 'verify_failed',
            detail: e?.response?.data || null,
          };
        }
      }
      setLiveViewResults(results);
      const allOk =
        Object.values(results).length > 0 &&
        Object.values(results).every((r: any) => r && r.ok && r.verified);
      setLiveViewStatus(allOk ? 'success' : 'error');
    } catch (err: any) {
      setLiveViewStatus('error');
      setError(err.response?.data?.error || 'Live view verification failed');
    }
  };

  const loadPipelines = async (deviceId: string, opts?: { silent?: boolean }) => {
    const silent = !!opts?.silent;
    if (!silent) setPipelineLoading(true);
    if (!silent && selectedDeviceIdRef.current === String(deviceId)) {
      setPipelineError(null);
    }
    try {
      const resp = await api.get(`/api/v1/registry/devices/${deviceId}/pipelines`);
      const data = resp.data;
      const cams: CameraPipelineStatus[] = (data.cameras || []).map((c: any) => ({
        camera_id: c.camera_id,
        pid: c.pid,
        alive: !!c.alive,
        pipeline: c.pipeline,
        source_type: c.source_type,
        rtsp_url: c.rtsp_url,
        runtime_desired_state: c.runtime_desired_state ?? null,
        runtime_override: c.runtime_override ?? null,
      }));
      if (selectedDeviceIdRef.current !== String(deviceId)) return;
      setPipelineCameras((prev) => {
        if (prev.length === cams.length) {
          let same = true;
          for (let i = 0; i < prev.length; i++) {
            const a = prev[i];
            const b = cams[i];
            if (
              a.camera_id !== b.camera_id ||
              !!a.alive !== !!b.alive ||
              (a.runtime_desired_state ?? null) !== (b.runtime_desired_state ?? null) ||
              (a.pipeline || '') !== (b.pipeline || '')
            ) {
              same = false;
              break;
            }
          }
          if (same) return prev;
        }
        return cams;
      });
      setPipelineRunningCount(Number(data.running_count || 0));
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Failed to fetch edge pipeline status';
      if (selectedDeviceIdRef.current === String(deviceId)) {
        setPipelineError(msg);
        setPipelineCameras([]);
        setPipelineRunningCount(0);
      }
    } finally {
      if (!silent) setPipelineLoading(false);
    }
  };

  // Poll pipeline status in the background once a device is selected.
  useEffect(() => {
    if (pipelinePollRef.current) {
      clearInterval(pipelinePollRef.current);
      pipelinePollRef.current = null;
    }
    if (!selectedDevice?.device_id) return;

    // Prime immediately (silent: avoid UI flicker)
    void loadPipelines(selectedDevice.device_id, { silent: true });
    pipelinePollRef.current = setInterval(() => {
      void loadPipelines(selectedDevice.device_id, { silent: true });
    }, 5000);

    return () => {
      if (pipelinePollRef.current) {
        clearInterval(pipelinePollRef.current);
        pipelinePollRef.current = null;
      }
    };
  }, [selectedDevice?.device_id]);

  const handleStopCameraPipeline = async (cameraId: string) => {
    if (!selectedDevice) return;
    setPipelineActionCameraId(cameraId);
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${selectedDevice.device_id}/edge/pipelines/cameras/${cameraId}/stop`, {});
      if (res?.data?.ok === false || res?.data?.error) {
        throw new Error(res?.data?.message || res?.data?.error || 'stop_failed');
      }
      await loadPipelines(selectedDevice.device_id);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'unknown error';
      const msg = `Failed to stop camera pipeline (${cameraId}): ${String(detail)}`;
      setPipelineError(msg);
      setError(msg);
    } finally {
      setPipelineActionCameraId(null);
    }
  };

  const handleStartCameraPipeline = async (cameraId: string) => {
    if (!selectedDevice) return;
    setPipelineActionCameraId(cameraId);
    try {
      const res = await api.post(`/api/v1/pipeline/devices/${selectedDevice.device_id}/edge/pipelines/cameras/${cameraId}/start`, {});
      if (res?.data?.ok === false || res?.data?.error) {
        throw new Error(res?.data?.message || res?.data?.error || 'start_failed');
      }
      await loadPipelines(selectedDevice.device_id);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'unknown error';
      const msg = `Failed to start camera pipeline (${cameraId}): ${String(detail)}`;
      setPipelineError(msg);
      setError(msg);
    } finally {
      setPipelineActionCameraId(null);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0: return !!selectedDevice;
      case 1: return cameras.length > 0;
      // Phase B policy system is org/site-scoped; wizard policy authoring is optional here.
      case 2: return true;
      case 3: return deployStatus === 'success';
      case 4: return liveViewStatus === 'success';
      case 5: return verifyStatus === 'success';
      default: return false;
    }
  };

  const _wizardStepKey = (deviceId: string) => `setup_wizard:last_step:${deviceId}`;

  const getSavedStep = (deviceId?: string | null) => {
    try {
      if (!deviceId) return null;
      const raw = localStorage.getItem(_wizardStepKey(deviceId));
      if (!raw) return null;
      const n = Number(raw);
      if (!Number.isFinite(n)) return null;
      if (n < 0 || n >= WIZARD_STEPS.length) return null;
      return n;
    } catch {
      return null;
    }
  };

  const computeRecommendedStep = () => {
    if (!selectedDevice) return 0;
    if (cameras.length === 0) return 1;
    if (selectedDeviceSync && selectedDeviceSync.in_sync === false) return 3;
    if (liveViewStatus !== 'success') return 4;
    return 5;
  };

  // Persist "last visited step" per device so we can resume later.
  useEffect(() => {
    try {
      if (!selectedDevice?.device_id) return;
      // Don't overwrite resume state just because the user opened the wizard.
      if (activeStep <= 0) return;
      localStorage.setItem(_wizardStepKey(selectedDevice.device_id), String(activeStep));
    } catch {
      // ignore
    }
  }, [activeStep, selectedDevice?.device_id]);

  // Render step content
  const renderStepContent = (step: number) => {
    const selectedDeviceZoneCount = selectedDeviceCounts?.zones ?? 0;
    const canAuthorPolicies = selectedDeviceZoneCount > 0 && canMutateSetup;
    const handleOpenZonesFromWizard = () => {
      const params = new URLSearchParams();
      if (selectedDevice?.device_id) {
        params.set('device_id', selectedDevice.device_id);
        params.set('return_device', selectedDevice.device_id);
      }
      const preferredCameraId =
        (newPolicy.camera_id || '').trim() || (cameras?.[0]?.camera_id || '').trim();
      if (preferredCameraId) {
        params.set('camera_id', preferredCameraId);
      }
      // Authoring zones is the immediate next action from Setup->Policies.
      params.set('open_snapshot', '1');
      params.set('return_to', 'setup');
      params.set('return_step', '2');
      navigate(`/zones?${params.toString()}`);
    };
    const handleOpenPoliciesFromWizard = (wizardPolicyType?: string) => {
      if (!selectedDevice) return;
      if (!canAuthorPolicies) return;
      const params = new URLSearchParams();
      params.set('create', '1');
      params.set('site', selectedDevice.site_id || 'default');
      params.set('device_id', selectedDevice.device_id);
      const preferredCameraId =
        (newPolicy.camera_id || '').trim() || (cameras?.[0]?.camera_id || '').trim();
      if (preferredCameraId) {
        params.set('camera_id', preferredCameraId);
      }
      if (wizardPolicyType) {
        const apiPolicyType =
          WIZARD_TO_API_POLICY_TYPE[wizardPolicyType] || wizardPolicyType;
        params.set('type', apiPolicyType);
      }
      params.set('return_to', 'setup');
      params.set('return_device', selectedDevice.device_id);
      params.set('return_step', '2');
      navigate(`/policies?${params.toString()}`);
    };
    switch (step) {
      case 0:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem' }}>
              Select an existing commissioned device to configure.
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Provisioning moved out of Setup Wizard</AlertTitle>
              <Typography variant="body2">
                Device registration, token generation, and claim are handled in Bring Up or Devices.
                Setup Wizard now picks up after commissioning to configure cameras, zones, pack-first policy staging,
                deploy, and verification for already provisioned devices.
              </Typography>
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="contained" href="/bringup">
                  Open Bring Up
                </Button>
                <Button size="small" variant="outlined" href="/devices">
                  Open Devices
                </Button>
              </Box>
            </Alert>

            {/* Device list */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 2, fontSize: '1rem' }}>Or Select Existing Device</Typography>
            <List sx={{ spacing: 2 }}>
              {devices.map((device) => (
                <ListItem
                  key={device.device_id}
                  button
                  selected={selectedDevice?.device_id === device.device_id}
                  onClick={() => handleSelectDevice(device)}
                  sx={{
                    borderRadius: 2,
                    mb: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: selectedDevice?.device_id === device.device_id ? 'primary.main' : 'grey.300',
                  }}
                >
                  <ListItemIcon>
                    <DeviceIcon color={device.status === 'online' || device.status === 'activated' ? 'success' : 'disabled'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={device.display_name || device.device_id}
                    secondary={`ID: ${device.device_id}`}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Status Chip */}
                    <Chip
                      size="small"
                      label={device.status === 'online' || device.status === 'activated' ? 'Online' : 'Pending'}
                      color={device.status === 'online' || device.status === 'activated' ? 'success' : 'warning'}
                    />
                    {canReadDeployments && (
                      <Chip
                        size="small"
                        label={productionReadyChipLabel(enterpriseJobsById[device.device_id])}
                        color={productionReadyChipColor(enterpriseJobsById[device.device_id])}
                      />
                    )}
                    {/* Check Status Button */}
                    <IconButton
                      size="small"
                      title="Check device status"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCheckDeviceStatus(device.device_id);
                      }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                    {/* Delete Button */}
                    <IconButton
                      size="small"
                      title="Delete device"
                      color="error"
                      disabled={!canWriteDevices}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteDevice(device.device_id);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
              {devices.length === 0 && (
                <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  No commissioned devices found yet. Start in Bring Up or Devices, then return here for staged setup.
                </Typography>
              )}
            </List>

            {/* Device summary (quick view) */}
            {selectedDevice && (
              <Card sx={{ mt: 3, border: '1px solid', borderColor: 'grey.300' }}>
                <CardContent>
                  {(() => {
                    const saved = getSavedStep(selectedDevice.device_id);
                    const recommended = computeRecommendedStep();
                    const resumeStep = (saved != null && saved > 0) ? saved : recommended;
                    const recommendedLabel = WIZARD_STEPS[recommended]?.label || 'Next';
                    const resumeLabel = WIZARD_STEPS[resumeStep]?.label || 'Continue';
                    const syncText =
                      typeof selectedDeviceSync?.in_sync === 'boolean'
                        ? ` • Sync: ${selectedDeviceSync.in_sync ? 'In sync' : 'Out of sync'}`
                        : '';
                    const selectedEnterpriseJob =
                      enterpriseJobsById[selectedDevice.device_id] ?? null;
                    const productionGateBlocked = isSetupContinueBlocked(
                      selectedEnterpriseJob,
                      canReadDeployments,
                    );

                    return (
                      <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        Device Summary: {selectedDevice.display_name || selectedDevice.device_id}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        ID: {selectedDevice.device_id}
                        {selectedDeviceStatus?.ip_address ? ` • IP: ${selectedDeviceStatus.ip_address}` : ''}
                        {typeof selectedDeviceStatus?.online === 'boolean' ? ` • ${selectedDeviceStatus.online ? 'Online' : 'Offline'}` : ''}
                        {syncText}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Recommended next: {recommendedLabel}
                        {saved != null && saved > 0 && saved !== recommended ? ` • Resume: ${WIZARD_STEPS[saved]?.label || ''}` : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setActiveStep(resumeStep)}
                        disabled={resumeStep === 0 || productionGateBlocked}
                      >
                        Continue: {resumeLabel}
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setActiveStep(1)}
                        disabled={productionGateBlocked}
                      >
                        Cameras
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setActiveStep(2)}
                        disabled={productionGateBlocked}
                      >
                        Policies
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setActiveStep(3)}
                        disabled={productionGateBlocked}
                      >
                        Deploy
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => setActiveStep(4)}
                        disabled={productionGateBlocked}
                      >
                        Verify
                      </Button>
                    </Box>
                  </Box>

                  <SetupProductionReadyGate
                    deviceId={selectedDevice.device_id}
                    deviceIp={selectedDeviceStatus?.ip_address}
                    enterpriseJob={selectedEnterpriseJob}
                    canReadDeployments={canReadDeployments}
                    canWriteDeployments={canWriteDeployments}
                    onJobChange={(j) =>
                      setEnterpriseJobsById((prev) => ({
                        ...prev,
                        [selectedDevice.device_id]: j,
                      }))
                    }
                  />

                  <Box sx={{ mt: 2 }}>
                    <Alert severity="info" sx={{ mb: 0 }}>
                      <AlertTitle>Preflight & Firmware</AlertTitle>
                      <Typography variant="body2">
                        Preflight is available as a dedicated page (single source of truth) with the full checklist, firmware details, and Update action.
                      </Typography>
                      <Button
                        size="small"
                        sx={{ mt: 1 }}
                        variant="contained"
                        href={`/preflight?device=${encodeURIComponent(selectedDevice.device_id)}`}
                      >
                        Open Preflight
                      </Button>
                    </Alert>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Cameras configured: {cameras.length}
                        </Typography>
                        {cameras.length > 0 ? (
                          <List dense sx={{ py: 0 }}>
                            {cameras.slice(0, 5).map((c: any) => (
                              <ListItem key={c.camera_id} sx={{ px: 0 }}>
                                <ListItemText
                                  primary={`${c.name || c.camera_id}`}
                                  secondary={`source: ${c.source_type || 'unknown'}`}
                                />
                              </ListItem>
                            ))}
                            {cameras.length > 5 && (
                              <Typography variant="caption" color="text.secondary">
                                + {cameras.length - 5} more…
                              </Typography>
                            )}
                          </List>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No cameras registered yet.
                          </Typography>
                        )}
                      </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Pipelines running: {pipelineRunningCount} / {pipelineCameras.length || cameras.length || 0}
                        </Typography>
                        {pipelineError ? (
                          <Typography variant="body2" color="error">
                            {pipelineError}
                          </Typography>
                        ) : pipelineCameras.length > 0 ? (
                          <List dense sx={{ py: 0 }}>
                            {pipelineCameras.slice(0, 5).map((p: any) => {
                              const isStoppedByOverride =
                                String(p.runtime_desired_state || '').toLowerCase() === 'stopped';
                              const canStart = isStoppedByOverride && !!selectedDevice;
                              const canStop = !isStoppedByOverride && !!selectedDevice;
                              return (
                              <ListItem key={p.camera_id} sx={{ px: 0 }}>
                                <ListItemText
                                  primary={`${cameraNameById[p.camera_id] || p.camera_id} • ${p.alive ? 'alive' : 'down'}`}
                                  secondary={`${p.pipeline || ''}${p.source_type ? ` • ${p.source_type}` : ''}`}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {isStoppedByOverride && (
                                    <Chip size="small" label="MANUAL STOP" color="warning" />
                                  )}
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    disabled={!canWriteDevices || !canStop || pipelineActionCameraId === p.camera_id}
                                    onClick={() => handleStopCameraPipeline(p.camera_id)}
                                  >
                                    Stop
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    disabled={!canWriteDevices || !canStart || pipelineActionCameraId === p.camera_id}
                                    onClick={() => handleStartCameraPipeline(p.camera_id)}
                                  >
                                    Start
                                  </Button>
                                </Box>
                              </ListItem>
                              );
                            })}
                            {pipelineCameras.length > 5 && (
                              <Typography variant="caption" color="text.secondary">
                                + {pipelineCameras.length - 5} more…
                              </Typography>
                            )}
                          </List>
                        ) : (
                          <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              No pipeline status reported yet. You can still Start/Stop pipelines (orchestrator-aware) per camera:
                            </Typography>
                            <List dense sx={{ py: 0 }}>
                              {cameras.slice(0, 5).map((c) => (
                                <ListItem key={c.camera_id} sx={{ px: 0 }}>
                                  <ListItemText
                                    primary={`${c.name || c.camera_id} • status unknown`}
                                    secondary="Deploy + Verify will show live status once the edge is reachable."
                                  />
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      disabled={!canWriteDevices || !selectedDevice || pipelineActionCameraId === c.camera_id}
                                      onClick={() => handleStopCameraPipeline(c.camera_id)}
                                    >
                                      Stop
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      disabled={!canWriteDevices || !selectedDevice || pipelineActionCameraId === c.camera_id}
                                      onClick={() => handleStartCameraPipeline(c.camera_id)}
                                    >
                                      Start
                                    </Button>
                                  </Box>
                                </ListItem>
                              ))}
                              {cameras.length > 5 && (
                                <Typography variant="caption" color="text.secondary">
                                  + {cameras.length - 5} more…
                                </Typography>
                              )}
                              {cameras.length === 0 && (
                                <Typography variant="body2" color="text.secondary">
                                  No cameras configured yet.
                                </Typography>
                              )}
                            </List>
                          </>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </Box>
        );

      case 1: {
        const sourceType = (newCamera.source_type || 'rtsp') as 'rtsp' | 'hardware';
        const existingIds = new Set((cameras || []).map((c) => c.camera_id));
        const trimmedUri = (newCamera.uri || '').trim();
        const resolvedHardwareId = ((newCamera.camera_id || 'hardware_cam') as string).trim() || 'hardware_cam';

        const rtspUriError =
          sourceType !== 'rtsp'
            ? ''
            : !trimmedUri
              ? 'RTSP URI is required'
              : !/^rtsps?:\/\//i.test(trimmedUri)
                ? 'Must start with rtsp:// or rtsps://'
                : /\/\/[^/]*@/.test(trimmedUri)
                  ? 'Remove username/password from URI (use fields below)'
                  : '';

        const hardwareIdError =
          sourceType !== 'hardware'
            ? ''
            : !/^[a-zA-Z0-9_-]+$/.test(resolvedHardwareId)
              ? 'Only letters/numbers/_/- allowed'
              : existingIds.has(resolvedHardwareId)
                ? `Camera ID "${resolvedHardwareId}" already exists`
                : '';

        const canAddCamera =
          sourceType === 'rtsp'
            ? !!trimmedUri && !rtspUriError
            : !hardwareIdError;

        return (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', flex: 1, minWidth: 0 }}>
                Add cameras to monitor. Use MAC address or Serial Number as the unique identifier.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setCameraDialogOpen(true)}
                disabled={!canWriteCameras}
                sx={{ ml: 'auto', flexShrink: 0 }}
              >
                Add Camera
              </Button>
            </Box>

            <List>
              {cameras.map((camera) => (
                <ListItem
                  key={camera.camera_id}
                  sx={{
                    borderRadius: 2,
                    mb: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                  }}
                >
                  <ListItemIcon>
                    <CameraIcon color={camera.status === 'online' ? 'success' : camera.status === 'offline' ? 'error' : 'disabled'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body1">{camera.name}</Typography>
                        {cameraTestSubmitting[camera.camera_id] ? (
                          <Chip size="small" color="info" label="Testing..." />
                        ) : (
                          <Chip
                            size="small"
                            color={
                              cameraTestResults[camera.camera_id]
                                ? cameraTestResults[camera.camera_id].ok
                                  ? 'success'
                                  : 'error'
                                : 'default'
                            }
                            label={
                              cameraTestResults[camera.camera_id]
                                ? cameraTestResults[camera.camera_id].ok
                                  ? 'Test: Yes'
                                  : 'Test: No'
                                : 'Test: Not run'
                            }
                            variant={cameraTestResults[camera.camera_id] ? 'filled' : 'outlined'}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        {camera.mac_address && (
                          <Typography variant="caption" component="div">
                            MAC: {camera.mac_address}
                          </Typography>
                        )}
                        {!camera.mac_address && camera.serial_number && (
                          <Typography variant="caption" component="div" color="text.secondary">
                            Serial: {camera.serial_number}
                          </Typography>
                        )}
                        <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                          {camera.uri}
                        </Typography>
                        {cameraTestResults[camera.camera_id] && (
                          <Typography
                            variant="caption"
                            component="div"
                            color={cameraTestResults[camera.camera_id].ok ? 'success.main' : 'error.main'}
                          >
                            {cameraTestResults[camera.camera_id].message} at{' '}
                            {new Date(cameraTestResults[camera.camera_id].testedAtMs).toLocaleTimeString()}
                          </Typography>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      onClick={() => handleTestCamera(camera.camera_id)}
                      title="Test connection"
                      disabled={!canWriteCameras || Boolean(cameraTestSubmitting[camera.camera_id])}
                    >
                      {cameraTestSubmitting[camera.camera_id] ? <CircularProgress size={18} /> : <TestIcon />}
                    </IconButton>
                    <IconButton onClick={() => handleDeleteCamera(camera.camera_id)} title="Remove" disabled={!canWriteCameras}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
              {cameras.length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <CameraIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography color="text.secondary">
                    No cameras configured. Add at least one camera.
                  </Typography>
                </Paper>
              )}
            </List>

            {/* Add Camera Dialog */}
            <Dialog open={cameraDialogOpen} onClose={() => setCameraDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Add Camera</DialogTitle>
              <DialogContent>
                <TextField
                  fullWidth
                  select
                  label="Source Type"
                  value={newCamera.source_type || 'rtsp'}
                  onChange={(e) => setNewCamera({ ...newCamera, source_type: e.target.value as any })}
                  sx={{ mt: 2, mb: 2 }}
                  helperText="Choose RTSP for network cameras or Hardware for CSI/board camera"
                >
                  <MenuItem value="rtsp">RTSP (Network Camera)</MenuItem>
                  <MenuItem value="hardware">Hardware (CSI / onboard camera)</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  label="MAC Address (Optional)"
                  placeholder="AA:BB:CC:DD:EE:FF"
                  value={newCamera.mac_address}
                  onChange={(e) => setNewCamera({ ...newCamera, mac_address: e.target.value })}
                  sx={{ mb: 2 }}
                  helperText="Hardware identifier - preferred if available"
                />
                <TextField
                  fullWidth
                  label="Serial Number (Optional)"
                  placeholder="PTZIP204WX4IR20220115CCWRS6J40873197"
                  value={newCamera.serial_number}
                  onChange={(e) => setNewCamera({ ...newCamera, serial_number: e.target.value })}
                  sx={{ mb: 2 }}
                  helperText="Alternative identifier - provide if MAC not available"
                />
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="caption">
                    MAC/Serial are optional, but recommended to generate a stable camera_id.
                  </Typography>
                </Alert>
                <TextField
                  fullWidth
                  label="Camera Name"
                  placeholder="Front Door Camera"
                  value={newCamera.name}
                  onChange={(e) => setNewCamera({ ...newCamera, name: e.target.value })}
                  sx={{ mb: 2 }}
                />
                {(newCamera.source_type || 'rtsp') === 'hardware' ? (
                  <TextField
                    fullWidth
                    label="Camera ID (optional)"
                    placeholder="hardware_cam"
                    value={(newCamera.camera_id || '') as any}
                    onChange={(e) => setNewCamera({ ...newCamera, camera_id: e.target.value })}
                    error={!!hardwareIdError}
                    helperText={hardwareIdError || 'Defaults to hardware_cam'}
                  />
                ) : (
                  <>
                    <TextField
                      fullWidth
                      label="RTSP URI (no password)"
                      placeholder="rtsp://192.168.3.40:554/Streaming/Channels/101/"
                      value={newCamera.uri}
                      onChange={(e) => setNewCamera({ ...newCamera, uri: e.target.value })}
                      error={!!rtspUriError}
                      helperText={rtspUriError || "Do not include username/password here. We'll store credentials separately."}
                      required
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="RTSP Username (optional)"
                      value={newCamera.rtsp_username || ''}
                      onChange={(e) => setNewCamera({ ...newCamera, rtsp_username: e.target.value })}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      fullWidth
                      label="RTSP Password (optional)"
                      type="password"
                      value={newCamera.rtsp_password || ''}
                      onChange={(e) => setNewCamera({ ...newCamera, rtsp_password: e.target.value })}
                    />
                  </>
                )}
              </DialogContent>
              <Box sx={{ px: 3, pb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  After adding, use the play icon on the camera row to run a feed test. The row shows explicit Test: Yes/No.
                </Typography>
              </Box>
              <DialogActions>
                <Button onClick={() => setCameraDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleAddCamera}
                  disabled={!canWriteCameras || !canAddCamera}
                >
                  Add Camera
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );
      }

      case 2:
        return (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                Configure safety detection policies for your cameras.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenPoliciesFromWizard(newPolicy.policy_type)}
                disabled={!canWritePolicies || !canAuthorPolicies}
              >
                Add Policy
              </Button>
            </Box>

            <Alert severity="info" sx={{ mb: 3 }}>
              <AlertTitle>Zones / ROI</AlertTitle>
              <Typography variant="body2">
                Zone/ROI configuration is managed in the Zones page (single source of truth). Use it for Zone Intrusion policies and overlays.
              </Typography>
              <Button
                size="small"
                variant="contained"
                sx={{ mt: 1 }}
                onClick={handleOpenZonesFromWizard}
                disabled={!canWritePolicies}
              >
                Open Zones
              </Button>
            </Alert>
            {!canMutateSetup && (
              <Alert severity="info" sx={{ mb: 3 }}>
                You have read-only setup access. Camera, policy, deploy, and pipeline mutations are disabled.
              </Alert>
            )}
            {canMutateSetup && !canAuthorPolicies && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                Create at least one zone (ROI) first for this device, then add policies.
              </Alert>
            )}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 2, fontSize: '1rem' }}>
              Configured Zones (ROI)
            </Typography>
            <List sx={{ mb: 1 }}>
              {selectedDeviceZones.map((z) => (
                <ListItem
                  key={`${z.camera_id}:${z.zone_id}`}
                  sx={{
                    borderRadius: 2,
                    mb: 1.5,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                  }}
                >
                  <ListItemIcon>
                    <PolicyIcon color={z.enabled ? 'primary' : 'disabled'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={z.name || z.zone_id}
                    secondary={`Zone ID: ${z.zone_id || '(missing)'}${z.camera_id ? ` • Camera: ${z.camera_id}` : ''}`}
                  />
                  <Chip
                    size="small"
                    label={z.enabled ? 'enabled' : 'disabled'}
                    color={z.enabled ? 'success' : 'default'}
                    variant={z.enabled ? 'filled' : 'outlined'}
                  />
                </ListItem>
              ))}
              {selectedDeviceZones.length === 0 && (
                <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <Typography color="text.secondary">
                    No zones found for this device. Click Open Zones to create one.
                  </Typography>
                </Paper>
              )}
            </List>

            {/* Policy type cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {POLICY_TYPES.map((pt) => (
                <Grid item xs={6} md={3} key={pt.type}>
                  <Card
                    sx={{
                      cursor: canAuthorPolicies ? 'pointer' : 'not-allowed',
                      '&:hover': { boxShadow: 3 },
                      border: '1px solid',
                      borderColor: 'grey.200',
                      opacity: canAuthorPolicies ? 1 : 0.5,
                    }}
                    onClick={() => {
                      if (!canAuthorPolicies) return;
                      handleOpenPoliciesFromWizard(pt.type);
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 2 }}>
                      <PolicyIcon color="primary" sx={{ mb: 1 }} />
                      <Typography variant="subtitle2">{pt.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {pt.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Configured policies */}
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 2, fontSize: '1rem' }}>
              Configured Policies
            </Typography>
            {hiddenStalePolicies > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {hiddenStalePolicies} stale polic{hiddenStalePolicies === 1 ? 'y was' : 'ies were'} hidden because they target old camera IDs not present on this device.
              </Alert>
            )}
            <List>
              {policies.map((policy) => (
                (() => {
                  const depMeta = getPolicyDeploymentMetadata(policy.deployment_state || 'DRAFT');
                  return (
                <ListItem
                  key={policy.policy_id}
                  sx={{
                    borderRadius: 2,
                    mb: 2,
                    p: 2,
                    border: '1px solid',
                    borderColor: 'grey.300',
                  }}
                >
                  <ListItemIcon>
                    <PolicyIcon color={policy.enabled ? 'primary' : 'disabled'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={policy.name}
                    secondary={`Type: ${policy.policy_type} • Severity: ${policy.severity}${policy.camera_id ? ` • Camera: ${policy.camera_id}` : ''}${policy.deployment_reason ? ` • ${policy.deployment_reason}` : ''}`}
                  />
                  <Chip
                    size="small"
                    label={depMeta.label}
                    color={depMeta.color}
                    variant="outlined"
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    size="small"
                    label={policy.severity}
                    color={policy.severity === 'critical' ? 'error' : policy.severity === 'high' ? 'warning' : 'default'}
                    sx={{ mr: 1 }}
                  />
                  <IconButton onClick={() => handleDeletePolicy(policy.policy_id)} disabled={!canWritePolicies}>
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
                  );
                })()
              ))}
              {policies.length === 0 && (
                <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50' }}>
                  <PolicyIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography color="text.secondary">
                    No policies configured. Add at least one policy.
                  </Typography>
                </Paper>
              )}
            </List>

            {/* Add Policy Dialog */}
            <Dialog open={policyDialogOpen} onClose={() => setPolicyDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Add Policy</DialogTitle>
              <DialogContent>
                {(() => {
                  const pt = POLICY_TYPES.find((x) => x.type === newPolicy.policy_type);
                  const tmpl = _policyTemplateFor(newPolicy.policy_type);
                  return (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <AlertTitle>{pt?.name || 'Policy'}</AlertTitle>
                      <Typography variant="body2">{pt?.description || ''}</Typography>
                      {tmpl.help ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {tmpl.help}
                        </Typography>
                      ) : null}
                    </Alert>
                  );
                })()}
                <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
                  <InputLabel>Policy Type</InputLabel>
                  <Select
                    value={newPolicy.policy_type}
                    label="Policy Type"
                    onChange={(e) => {
                      const nextType = String(e.target.value);
                      const tmpl = _policyTemplateFor(nextType);
                      setNewPolicy({
                        ...newPolicy,
                        policy_type: nextType,
                        severity: tmpl.defaultSeverity,
                        name: newPolicy.name?.trim() ? newPolicy.name : `${tmpl.namePrefix} Policy`,
                      });
                    }}
                  >
                    {POLICY_TYPES.map((pt) => (
                      <MenuItem key={pt.type} value={pt.type}>{pt.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Policy Name"
                  value={newPolicy.name}
                  onChange={(e) => setNewPolicy({ ...newPolicy, name: e.target.value })}
                  sx={{ mb: 2 }}
                />
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Severity</InputLabel>
                  <Select
                    value={newPolicy.severity}
                    label="Severity"
                    onChange={(e) => setNewPolicy({ ...newPolicy, severity: e.target.value })}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="critical">Critical</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Camera</InputLabel>
                  <Select
                    value={newPolicy.camera_id}
                    label="Camera"
                    onChange={(e) => setNewPolicy({ ...newPolicy, camera_id: String(e.target.value || '') })}
                  >
                    {cameras.map((cam) => (
                      <MenuItem key={cam.camera_id} value={cam.camera_id}>{cam.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setPolicyDialogOpen(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleAddPolicy}
                  disabled={!canWritePolicies || !newPolicy.name.trim() || !newPolicy.camera_id}
                >
                  Add Policy
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
              Deploy the configuration to the edge device.
            </Typography>

            {/* Summary */}
            <Paper sx={{ p: 3, mb: 4, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>Configuration Summary</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {(selectedDevice?.org_id || selectedDevice?.site_id) && (
                  <Chip
                    size="small"
                    label={`Org: ${selectedDevice?.org_id || 'default'} | Site: ${selectedDevice?.site_id || 'default'}`}
                    color="default"
                    variant="outlined"
                  />
                )}
                {scopeBlastRadius && (
                  <Chip
                    size="small"
                    label={
                      scopeCanaryCount != null
                        ? `Blast radius: 1 device (this deploy) • Site: ${scopeBlastRadius.device_count} • Canary: ${scopeCanaryCount}`
                        : `Blast radius: 1 device (this deploy) • Site has ${scopeBlastRadius.device_count} device(s)`
                    }
                    color="info"
                    variant="outlined"
                  />
                )}
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Typography variant="h4" color="primary">{cameras.length}</Typography>
                  <Typography variant="caption">Cameras</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h4" color="primary">{policies.length}</Typography>
                  <Typography variant="caption">Policies</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="h4" color="primary">{selectedDevice?.device_id.slice(0, 8)}</Typography>
                  <Typography variant="caption">Device</Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Preview changes */}
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle2">Preview Deploy Changes</Typography>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleDeployPreview}
                  disabled={!selectedDevice || deployPreviewLoading}
                >
                  Preview
                </Button>
              </Box>
              {deployPreviewLoading && <LinearProgress sx={{ mt: 2 }} />}
              {deployPreviewError && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {deployPreviewError}
                </Alert>
              )}
              {deployPreview && (
                <Alert severity={deployPreview.would_change ? 'info' : 'success'} sx={{ mt: 2 }}>
                  <AlertTitle>
                    {deployPreview.would_change ? 'Changes detected' : 'No changes since last deploy'}
                  </AlertTitle>
                  <Typography variant="body2">
                    Current revision: {deployPreview.current_revision || 'none'} • Next deploy will {deployPreview.would_change ? 'update' : 'not change'} the bundle.
                  </Typography>
                </Alert>
              )}
            </Paper>

            {/* Deploy status */}
            {deployStatus === 'idle' && (
              <Button
                variant="contained"
                size="large"
                startIcon={<DeployIcon />}
                onClick={handleDeploy}
                fullWidth
                disabled={!canWriteDeployments || (deployPreview && deployPreview.would_change === false)}
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                Deploy Configuration
              </Button>
            )}
            {deployStatus === 'idle' && deployPreview && deployPreview.would_change === false && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleDeploy}
                fullWidth
                disabled={!canWriteDeployments}
                sx={{ mt: 1 }}
              >
                Force Deploy (create new revision anyway)
              </Button>
            )}

            {deployStatus === 'deploying' && (
              <Box sx={{ textAlign: 'center' }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography>{deployMessage}</Typography>
              </Box>
            )}

            {deployStatus === 'success' && (
              <Alert severity="success">
                <AlertTitle>Deployment Successful</AlertTitle>
                {deployMessage}
              </Alert>
            )}

            {/* Gate 4 convenience: export site config + show CLI guidance */}
            <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Gate 4 Site Config (export + CLI apply/validate)
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                For pilots, prefer the CLI as the source of truth. You can export the current cloud state as a starting point.
              </Typography>
              {exportSiteConfigError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {exportSiteConfigError}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportSiteConfig}
                  disabled={!selectedDevice || exportingSiteConfig}
                >
                  Export current site config
                </Button>
              </Box>
              {exportingSiteConfig && <LinearProgress sx={{ mt: 2 }} />}
            </Paper>

            <Dialog open={siteConfigDialogOpen} onClose={() => setSiteConfigDialogOpen(false)} maxWidth="md" fullWidth>
              <DialogTitle>Exported Site Config (JSON)</DialogTitle>
              <DialogContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                  This is a convenience export. For pilot-grade repeatability, apply/validate via the CLI:
                  <br />
                  <code>python3 scripts/site_config_apply.py --api http://&lt;cloud&gt;:8000 --site-config ...</code>
                  <br />
                  <code>python3 scripts/site_config_validate.py --api http://&lt;cloud&gt;:8000 --site-config ... --device-id ...</code>
                </Alert>
                <TextField
                  fullWidth
                  multiline
                  minRows={14}
                  value={siteConfigJson ? JSON.stringify(siteConfigJson, null, 2) : ''}
                  inputProps={{ readOnly: true }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSiteConfigDialogOpen(false)}>Close</Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (!siteConfigJson) return;
                    const siteId = siteConfigJson.site_id || 'default';
                    downloadJson(`site_config_${siteId}.json`, siteConfigJson);
                  }}
                >
                  Download JSON
                </Button>
              </DialogActions>
            </Dialog>

            {/* Per-camera pipeline status panel (Model A) */}
            <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">Pipelines (one per camera)</Typography>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => selectedDevice && loadPipelines(selectedDevice.device_id)}
                  disabled={!selectedDevice || pipelineLoading}
                >
                  Refresh
                </Button>
              </Box>

              {pipelineLoading && <LinearProgress sx={{ mb: 2 }} />}
              {pipelineError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {pipelineError}
                </Alert>
              )}

              {(() => {
                const byId: Record<string, CameraPipelineStatus> = {};
                for (const c of pipelineCameras) byId[c.camera_id] = c;
                const rows = (cameras || []).map((cam) => ({ cam, runtime: byId[cam.camera_id] }));
                const running = rows.filter((r) => r.runtime?.alive === true).length;
                return (
                  <>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Running: <strong>{running}</strong> / {rows.length || 0}
                    </Typography>

                    <List>
                      {rows.map(({ cam, runtime }) => {
                        const aliveValue = runtime?.alive;
                        const aliveKnown = typeof aliveValue === 'boolean';
                        const alive = aliveValue === true;
                        const stoppedByOverride =
                          String(runtime?.runtime_desired_state || '').toLowerCase() === 'stopped';
                        const runtimeLabel = !aliveKnown ? 'UNKNOWN' : alive ? 'RUNNING' : 'STOPPED';
                        const pipelineName = runtime?.pipeline || 'not yet reported';
                        const sourceType =
                          runtime?.source_type ||
                          (String(cam.uri || '').toLowerCase().startsWith('rtsp') ? 'rtsp' : 'unknown');
                        const rtspUrl = runtime?.rtsp_url || (String(cam.uri || '').trim() || null);

                        return (
                          <ListItem
                            key={cam.camera_id}
                            sx={{ borderRadius: 2, mb: 1, border: '1px solid', borderColor: 'grey.300' }}
                          >
                            <ListItemIcon>
                              <CameraIcon color={alive ? 'success' : 'disabled'} />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${cam.name || cam.camera_id} (${cam.camera_id})`}
                              secondary={
                                <>
                                  <Typography variant="caption" component="div">
                                    pipeline: {pipelineName} • source: {sourceType}
                                  </Typography>
                                  {rtspUrl && (
                                    <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace' }}>
                                      {rtspUrl}
                                    </Typography>
                                  )}
                                </>
                              }
                            />
                            <Chip
                              size="small"
                              label={runtimeLabel}
                              color={alive ? 'success' : 'default'}
                              variant={!aliveKnown ? 'outlined' : 'filled'}
                            />
                            {stoppedByOverride && (
                              <Chip
                                size="small"
                                label="MANUAL STOP"
                                color="warning"
                                sx={{ ml: 1 }}
                                title={(runtime?.runtime_override || {})?.reason || 'Stopped by runtime override'}
                              />
                            )}
                            <Box sx={{ display: 'flex', gap: 1, ml: 2, flexWrap: 'wrap' }}>
                              {aliveKnown && alive && !stoppedByOverride ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={!canWriteDevices || !selectedDevice || pipelineActionCameraId === cam.camera_id}
                                  onClick={() => handleStopCameraPipeline(cam.camera_id)}
                                >
                                  Stop
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!canWriteDevices || !selectedDevice || pipelineActionCameraId === cam.camera_id}
                                  onClick={() => handleStartCameraPipeline(cam.camera_id)}
                                >
                                  Start
                                </Button>
                              )}
                            </Box>
                          </ListItem>
                        );
                      })}
                      {rows.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 1 }}>
                          No configured cameras yet.
                        </Typography>
                      )}
                      {rows.length > 0 && pipelineCameras.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 1 }}>
                          No pipeline processes reported yet. (After deploy, the edge poller + orchestrator may take a few seconds.)
                        </Typography>
                      )}
                    </List>
                  </>
                );
              })()}
            </Paper>

            {deployStatus === 'error' && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={handleDeploy} disabled={!canWriteDeployments}>
                    Retry
                  </Button>
                }
              >
                <AlertTitle>Deployment Failed</AlertTitle>
                {deployMessage}
              </Alert>
            )}
          </Box>
        );

      case 4:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
              Verify live view end-to-end (ingest → output → view). This runs per-camera probes and confirms the client can render a first MJPEG frame.
            </Typography>

            {liveViewStatus === 'idle' && (
              <Button
                variant="contained"
                size="large"
                startIcon={<TestIcon />}
                onClick={handleVerifyLiveView}
                fullWidth
                sx={{ py: 1.5, fontSize: '1rem' }}
                disabled={!selectedDevice || cameras.length === 0}
              >
                Run Live View Verification
              </Button>
            )}

            {liveViewStatus === 'checking' && (
              <Box sx={{ textAlign: 'center' }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography>Running per-camera probes…</Typography>
              </Box>
            )}

            {(liveViewStatus === 'success' || liveViewStatus === 'error') && (
              <>
                <Alert severity={liveViewStatus === 'success' ? 'success' : 'warning'} sx={{ mb: 2 }}>
                  <AlertTitle>{liveViewStatus === 'success' ? 'Live View Verified' : 'Live View Issues Detected'}</AlertTitle>
                  <Typography variant="body2">
                    {liveViewStatus === 'success'
                      ? 'All cameras passed ingest, output, and view probes.'
                      : 'One or more cameras failed a probe. Expand results below for details.'}
                  </Typography>
                </Alert>

                <Paper sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Per-Camera Results</Typography>
                  <List>
                    {(cameras || []).map((cam) => {
                      const r: any = (liveViewResults || {})[cam.camera_id];
                      const verified = !!r?.verified;
                      const ingestOk = !!r?.probes?.ingest?.ok;
                      const outputOk = !!r?.probes?.output?.ok;
                      const viewOk = !!r?.probes?.view?.ok;
                      const domain = r?.probes?.ingest?.ok === false ? 'INGEST'
                        : r?.probes?.output?.ok === false ? 'OUTPUT'
                        : r?.probes?.view?.ok === false ? 'VIEW'
                        : verified ? 'OK'
                        : 'UNKNOWN';

                      return (
                        <ListItem key={cam.camera_id} sx={{ borderRadius: 2, mb: 1, border: '1px solid', borderColor: 'grey.300' }}>
                          <ListItemIcon>
                            <CameraIcon color={verified ? 'success' : 'disabled'} />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${cam.name || cam.camera_id} (${cam.camera_id})`}
                            secondary={
                              <Typography variant="caption" component="div">
                                ingest: {String(ingestOk)} • output: {String(outputOk)} • view: {String(viewOk)} • domain: {domain}
                              </Typography>
                            }
                          />
                          <Chip
                            size="small"
                            label={verified ? 'VERIFIED' : 'NOT VERIFIED'}
                            color={verified ? 'success' : 'warning'}
                            variant="filled"
                          />
                        </ListItem>
                      );
                    })}
                    {cameras.length === 0 && (
                      <Typography color="text.secondary" sx={{ py: 1 }}>
                        Configure cameras first.
                      </Typography>
                    )}
                  </List>
                </Paper>

                {liveViewStatus === 'error' && (
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={handleVerifyLiveView}
                    sx={{ mt: 2 }}
                  >
                    Retry Live View Verification
                  </Button>
                )}
              </>
            )}
          </Box>
        );

      case 5:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="body1" sx={{ mb: 4, fontSize: '1.1rem' }}>
              Verify the edge device received the configuration and is online.
            </Typography>

            {verifyStatus === 'idle' && (
              <Button
                variant="contained"
                size="large"
                startIcon={<RefreshIcon />}
                onClick={handleVerify}
                fullWidth
                sx={{ py: 1.5, fontSize: '1rem' }}
              >
                Verify Connection
              </Button>
            )}

            {verifyStatus === 'checking' && (
              <Box sx={{ textAlign: 'center' }}>
                <LinearProgress sx={{ mb: 2 }} />
                <Typography>Checking device status...</Typography>
              </Box>
            )}

            {(verifyStatus === 'success' || verifyStatus === 'error') && verifyResults && (
              <>
                {(() => {
                  const ip = verifyResults.ip_address;
                  const edgeReachable = !!verifyResults.edge_reachable;
                  const online = !!verifyResults.online;
                  const _inSync =
                    typeof verifyResults.in_sync === 'boolean'
                      ? verifyResults.in_sync
                      : (verifyResults.config_revision && verifyResults.desired_revision)
                        ? verifyResults.config_revision === verifyResults.desired_revision
                        : null;

                  // Highest-signal classification for operator guidance.
                  if (!ip) {
                    return (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>Waiting for device IP</AlertTitle>
                        <Typography variant="body2">
                          The cloud does not have an IP address for this device yet, so it cannot reach the edge health API.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Next steps: complete device activation on the edge, ensure it can reach the cloud, then retry.
                        </Typography>
                      </Alert>
                    );
                  }

                  if (!edgeReachable) {
                    return (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        <AlertTitle>Edge health API not reachable</AlertTitle>
                        <Typography variant="body2">
                          Cloud can’t reach the edge health API at {ip}. Check network routing/firewall and the configured health API port.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          If the device is on, verify it’s on the same LAN and that the edge health service is running.
                        </Typography>
                      </Alert>
                    );
                  }

                  if (online) {
                    return (
                      <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
                        <AlertTitle>Device Online!</AlertTitle>
                        <Typography variant="body2">IP: {ip}</Typography>
                        <Typography variant="body2">Last seen: {verifyResults.last_seen || 'Just now'}</Typography>
                      </Alert>
                    );
                  }

                  return (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <AlertTitle>Device Offline</AlertTitle>
                      <Typography variant="body2">
                        The device appears offline. Ensure it is powered on and connected to the network.
                      </Typography>
                    </Alert>
                  );
                })()}

                <Paper sx={{ p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Connection Details</Typography>
                  <Typography variant="body2">Device ID: <strong>{verifyResults.device_id || selectedDevice?.device_id}</strong></Typography>
                  <Typography variant="body2">IP: <strong>{verifyResults.ip_address || 'unknown'}</strong></Typography>
                  <Typography variant="body2">Edge reachable: <strong>{String(!!verifyResults.edge_reachable)}</strong></Typography>
                  <Typography variant="body2">Online: <strong>{String(!!verifyResults.online)}</strong></Typography>
                  <Typography variant="body2">Config revision (reported): <strong>{verifyResults.config_revision || 'none'}</strong></Typography>
                  <Typography variant="body2">Desired revision: <strong>{verifyResults.desired_revision || 'none'}</strong></Typography>
                  {typeof verifyResults.in_sync === 'boolean' && (
                    <Typography variant="body2">In sync: <strong>{String(verifyResults.in_sync)}</strong></Typography>
                  )}

                  {verifyResults.in_sync === false && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <AlertTitle>Config not applied yet</AlertTitle>
                      <Typography variant="body2">
                        The edge is reachable, but it hasn’t applied the latest desired configuration.
                      </Typography>
                      <Button size="small" sx={{ mt: 1 }} variant="contained" onClick={() => setActiveStep(3)}>
                        Go to Deploy
                      </Button>
                    </Alert>
                  )}
                </Paper>
              </>
            )}

            {/* Pipeline status quick view */}
            <Paper sx={{ p: 3, mt: 3, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2">Pipelines</Typography>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => selectedDevice && loadPipelines(selectedDevice.device_id)}
                  disabled={!selectedDevice || pipelineLoading}
                >
                  Refresh
                </Button>
              </Box>
              {pipelineLoading && <LinearProgress sx={{ mb: 2 }} />}
              {pipelineError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {pipelineError}
                </Alert>
              )}
              {(() => {
                const byId: Record<string, CameraPipelineStatus> = {};
                for (const c of pipelineCameras) byId[c.camera_id] = c;
                const rows = (cameras || []).map((cam) => ({ cam, runtime: byId[cam.camera_id] }));
                const running = rows.filter((r) => r.runtime?.alive === true).length;
                return (
                  <>
                    <Typography variant="body2">
                      Running: <strong>{running}</strong> / {rows.length || 0}
                    </Typography>
                    <List sx={{ mt: 1 }}>
                      {rows.map(({ cam, runtime }) => {
                        const aliveValue = runtime?.alive;
                        const aliveKnown = typeof aliveValue === 'boolean';
                        const alive = aliveValue === true;
                        const stoppedByOverride =
                          String(runtime?.runtime_desired_state || '').toLowerCase() === 'stopped';
                        const runtimeLabel = !aliveKnown ? 'UNKNOWN' : alive ? 'RUNNING' : 'STOPPED';
                        const pipelineName = runtime?.pipeline || 'not yet reported';
                        const sourceType =
                          runtime?.source_type ||
                          (String(cam.uri || '').toLowerCase().startsWith('rtsp') ? 'rtsp' : 'unknown');

                        return (
                          <ListItem
                            key={cam.camera_id}
                            sx={{ borderRadius: 2, mb: 1, border: '1px solid', borderColor: 'grey.300' }}
                          >
                            <ListItemIcon>
                              <CameraIcon color={alive ? 'success' : 'disabled'} />
                            </ListItemIcon>
                            <ListItemText
                              primary={`${cam.name || cam.camera_id} (${cam.camera_id})`}
                              secondary={`${pipelineName} • ${sourceType}`}
                            />
                            <Chip
                              size="small"
                              label={runtimeLabel}
                              color={alive ? 'success' : 'default'}
                              variant={!aliveKnown ? 'outlined' : 'filled'}
                              sx={{ mr: 1 }}
                            />
                            {stoppedByOverride && (
                              <Chip size="small" label="MANUAL STOP" color="warning" sx={{ mr: 1 }} />
                            )}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {aliveKnown && alive && !stoppedByOverride ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  disabled={!canWriteDevices || !selectedDevice || pipelineActionCameraId === cam.camera_id}
                                  onClick={() => handleStopCameraPipeline(cam.camera_id)}
                                >
                                  Stop
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!canWriteDevices || !selectedDevice || pipelineActionCameraId === cam.camera_id}
                                  onClick={() => handleStartCameraPipeline(cam.camera_id)}
                                >
                                  Start
                                </Button>
                              )}
                            </Box>
                          </ListItem>
                        );
                      })}
                      {rows.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 1 }}>
                          Configure cameras first.
                        </Typography>
                      )}
                      {rows.length > 0 && pipelineCameras.length === 0 && (
                        <Typography color="text.secondary" sx={{ py: 1 }}>
                          No pipelines reported yet. (After deploy, the edge poller + orchestrator may take a few seconds.)
                        </Typography>
                      )}
                    </List>
                  </>
                );
              })()}
            </Paper>

            {verifyStatus === 'error' && (
              <Alert
                severity="error"
                action={
                  <Button color="inherit" size="small" onClick={handleVerify}>
                    Retry
                  </Button>
                }
              >
                <AlertTitle>Device Offline</AlertTitle>
                {verifyResults?.error || 'Unable to reach device. Ensure it is powered on and connected to the network.'}
              </Alert>
            )}

            {verifyStatus === 'success' && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>Setup Complete!</Typography>
                <Typography color="text.secondary">
                  Your edge device is configured and ready to monitor safety incidents.
                </Typography>
              </Box>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 4, flexGrow: 1, maxWidth: '1400px', mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
            Setup Wizard
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 5 }}>
            Post-provision setup flow for cameras, zones, pack-driven policies, deploy, and verification.
          </Typography>
        </Box>
        <Tooltip title="Open documentation (Setup Wizard)">
          <IconButton
            size="small"
            component="a"
            href="/docs/user-guide/setup-options/#setup-wizard-guided"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mt: 0.5 }}
          >
            <DocsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 4 }}>
          {error}
        </Alert>
      )}

      {!canMutateSetup && (
        <Alert severity="info" sx={{ mb: 4 }}>
          You have read-only setup access. Setup mutations are disabled.
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6">Pilot mode governance</Typography>
              <Typography variant="body2" color="text.secondary">
                Soft caps + warnings for pilots (does not hard-block creation).
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={pilotMode}
                  onChange={(e) => void savePilotConfig(Boolean(e.target.checked), pilotCaps)}
                  disabled={!canWriteDeployments || pilotSaving}
                />
              }
              label={pilotMode ? 'Pilot mode ON' : 'Pilot mode OFF'}
            />
          </Box>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Max zones (warn)"
                type="number"
                value={pilotCaps.max_zones}
                onChange={(e) => setPilotCaps((p) => ({ ...p, max_zones: Number(e.target.value || 0) }))}
                fullWidth
                size="small"
                disabled={!canWriteDeployments}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Max policies (warn)"
                type="number"
                value={pilotCaps.max_policies}
                onChange={(e) => setPilotCaps((p) => ({ ...p, max_policies: Number(e.target.value || 0) }))}
                fullWidth
                size="small"
                disabled={!canWriteDeployments}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Max policies per camera (warn)"
                type="number"
                value={pilotCaps.max_policies_per_camera}
                onChange={(e) => setPilotCaps((p) => ({ ...p, max_policies_per_camera: Number(e.target.value || 0) }))}
                fullWidth
                size="small"
                disabled={!canWriteDeployments}
              />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" onClick={() => void savePilotConfig(pilotMode, pilotCaps)} disabled={!canWriteDeployments || pilotSaving}>
                Save governance settings
              </Button>
            </Grid>
          </Grid>

          {complexity?.ok && (
            <Alert severity={pilotMode && (complexity.warnings || []).length > 0 ? 'warning' : 'info'} sx={{ mt: 2 }}>
              <AlertTitle>Site Complexity</AlertTitle>
              <Typography variant="body2" color="text.secondary">
                Site-level totals across all devices in site: <strong>{selectedDevice?.site_id || complexity.site_id || 'default'}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                zones={complexity.counts.zones}, policies={complexity.counts.policies}, max_policies_per_camera={complexity.counts.max_policies_per_camera}, score={complexity.complexity_score}
              </Typography>
              {selectedDevice?.device_id && selectedDeviceCounts && (
                <Typography variant="body2" color="text.secondary">
                  Selected device ({selectedDevice.device_id}): zones={selectedDeviceCounts.zones}, policies={selectedDeviceCounts.policies}
                </Typography>
              )}
              {pilotMode && (complexity.warnings || []).length > 0 && (
                <List dense>
                  {(complexity.warnings || []).slice(0, 5).map((w, idx) => (
                    <ListItem key={`${w.kind}-${idx}`}>
                      <ListItemIcon>
                        <WarningIcon color="warning" />
                      </ListItemIcon>
                      <ListItemText
                        primary={w.kind}
                        secondary={`${w.detail || ''}${w.recommended ? ` • ${w.recommended}` : ''}`}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card sx={{ maxWidth: 'none', boxShadow: 3 }}>
        <CardContent sx={{ maxWidth: 'none', p: 4 }}>
          {/* Horizontal stepper (prevents vertical StepContent indent/squeeze) */}
          <Stepper activeStep={activeStep} alternativeLabel sx={{ width: '100%', mb: 4 }}>
            {WIZARD_STEPS.map((step, index) => (
              <Step key={step.label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                        color: 'white',
                      }}
                    >
                      {index < activeStep ? <CheckIcon fontSize="small" /> : step.icon}
                    </Box>
                  )}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Full-width step content */}
          <Box sx={{ width: '100%' }}>
            {renderStepContent(activeStep)}
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            {activeStep < WIZARD_STEPS.length - 1 ? (
              <Button variant="contained" onClick={handleNext} disabled={!canProceed() || loading}>
                Continue
              </Button>
            ) : (
              <Button variant="contained" color="success" href="/devices" disabled={!canProceed()}>
                Finish & Go to Devices
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      <Snackbar
        open={cameraTestToast.open}
        autoHideDuration={4000}
        onClose={() => setCameraTestToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setCameraTestToast((prev) => ({ ...prev, open: false }))}
          severity={cameraTestToast.severity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {cameraTestToast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SetupWizard;
